import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, ChevronDown, TrendingUp, Lock, FileEdit, List, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  usePositions,
  usePositionUsers,
  useCreatePosition,
  useClosePosition,
  useUpdatePosition,
  useReopenPosition,
} from '@/hooks/usePositions'
import { useAuth } from '@/hooks/useAuth'
import type { PositionUser } from '@/types'

export type PositionManagerAction = 'create' | 'update' | 'close' | 'reopen'

export interface PositionActionRequest {
  requestId: number
  action: PositionManagerAction
  positionId?: string
  symbol?: string
}

interface PositionManagerProps {
  actionRequest?: PositionActionRequest | null
  hideTrigger?: boolean
}

type ModalType = PositionManagerAction | null

function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

interface UserCheckboxListProps {
  users: PositionUser[]
  selectedUserIds: Set<string>
  onToggle: (userId: string) => void
  disabled?: boolean
  emptyMessage?: string
}

function UserCheckboxList({ users, selectedUserIds, onToggle, disabled, emptyMessage = 'No users available' }: UserCheckboxListProps) {
  if (users.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground text-sm italic">
        {emptyMessage}
      </div>
    )
  }

  return (
    <ScrollArea className="h-[150px] border rounded-md">
      <div className="p-2 space-y-1">
        {users.map((user) => (
          <label
            key={user.id}
            className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedUserIds.has(user.id)}
              onChange={() => onToggle(user.id)}
              disabled={disabled}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm">
              @{user.username}
              {user.name !== user.username && ` (${user.name})`}
            </span>
          </label>
        ))}
      </div>
    </ScrollArea>
  )
}

export function PositionManager({ actionRequest, hideTrigger = false }: PositionManagerProps = {}) {
  const { hasPermission } = useAuth()
  const { data: positionsData, refetch: refetchPositions } = usePositions()
  const { data: usersData } = usePositionUsers()

  const createPosition = useCreatePosition()
  const closePositionMutation = useClosePosition()
  const updatePositionMutation = useUpdatePosition()
  const reopenPositionMutation = useReopenPosition()

  const [modalType, setModalType] = useState<ModalType>(null)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  // Create modal state
  const [createSymbol, setCreateSymbol] = useState('')
  const [createDecisionReason, setCreateDecisionReason] = useState('')
  const [createDirection, setCreateDirection] = useState<'na' | 'long' | 'short'>('na')
  const [createSelectedUsers, setCreateSelectedUsers] = useState<Set<string>>(new Set())

  // Close modal state
  const [closeSelectedPositionId, setCloseSelectedPositionId] = useState('')
  const [closeReason, setCloseReason] = useState('')
  const [closeResult, setCloseResult] = useState<'profit' | 'loss' | ''>('')

  // Update modal state
  const [updateSelectedPositionId, setUpdateSelectedPositionId] = useState('')
  const [updateSelectedUsers, setUpdateSelectedUsers] = useState<Set<string>>(new Set())
  const [updateNote, setUpdateNote] = useState('')

  // Reopen modal state
  const [reopenSelectedPositionId, setReopenSelectedPositionId] = useState('')
  const [reopenReason, setReopenReason] = useState('')

  const allPositions = useMemo(() => positionsData?.positions || [], [positionsData?.positions])
  const openPositions = useMemo(
    () => allPositions.filter(position => position.status === 'open'),
    [allPositions],
  )
  const closedPositions = useMemo(
    () => allPositions.filter(position => position.status === 'closed'),
    [allPositions],
  )
  const availableUsers = useMemo(() => usersData?.users || [], [usersData?.users])

  const hasPositionPermission = hasPermission('manage_positions')

  // Get the selected position for update modal
  const selectedPositionForUpdate = useMemo(() => {
    return allPositions.find(p => p.id === updateSelectedPositionId)
  }, [allPositions, updateSelectedPositionId])

  // Reset modal state when closing
  const closeModal = () => {
    setModalType(null)
    setResult(null)
    // Reset create modal
    setCreateSymbol('')
    setCreateDecisionReason('')
    setCreateDirection('na')
    setCreateSelectedUsers(new Set())
    // Reset close modal
    setCloseSelectedPositionId('')
    setCloseReason('')
    setCloseResult('')
    // Reset update modal
    setUpdateSelectedPositionId('')
    setUpdateSelectedUsers(new Set())
    setUpdateNote('')
    // Reset reopen modal
    setReopenSelectedPositionId('')
    setReopenReason('')
  }

  // When selecting a position for update, pre-select existing users
  useEffect(() => {
    if (selectedPositionForUpdate && availableUsers.length > 0) {
      const currentUsernames = new Set(selectedPositionForUpdate.users_in_position.map(u => u.toLowerCase()))
      const selectedIds = new Set<string>()
      availableUsers.forEach(u => {
        if (currentUsernames.has(u.username.toLowerCase())) {
          selectedIds.add(u.id)
        }
      })
      setUpdateSelectedUsers(selectedIds)
    } else {
      setUpdateSelectedUsers(new Set())
    }
  }, [selectedPositionForUpdate, availableUsers])

  const handleOpenModal = useCallback((type: ModalType, positionId?: string, symbol?: string) => {
    if (type === 'create' && symbol) setCreateSymbol(symbol.toUpperCase())
    if (type === 'update') setUpdateSelectedPositionId(positionId || '')
    if (type === 'close') setCloseSelectedPositionId(positionId || '')
    if (type === 'reopen') setReopenSelectedPositionId(positionId || '')
    setModalType(type)
    setResult(null)
    refetchPositions()
  }, [refetchPositions])

  useEffect(() => {
    if (!actionRequest) return
    handleOpenModal(actionRequest.action, actionRequest.positionId, actionRequest.symbol)
  }, [actionRequest, handleOpenModal])

  const toggleCreateUser = (userId: string) => {
    setCreateSelectedUsers(prev => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const toggleUpdateUser = (userId: string) => {
    setUpdateSelectedUsers(prev => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const handleCreateSubmit = async () => {
    if (!createSymbol.trim()) {
      setResult({ success: false, message: 'Symbol is required' })
      return
    }
    if (!createDecisionReason.trim()) {
      setResult({ success: false, message: 'Decision rationale is required' })
      return
    }

    try {
      const response = await createPosition.mutateAsync({
        symbol: createSymbol.trim().toUpperCase(),
        direction: createDirection === 'na' ? null : createDirection,
        decision_reason: createDecisionReason.trim(),
        users_in_position: Array.from(createSelectedUsers),
      })

      if (response.ok) {
        setResult({ success: true, message: 'Discussion created successfully!' })
        setTimeout(closeModal, 1500)
      } else {
        setResult({ success: false, message: response.error || 'Failed to create discussion' })
      }
    } catch (error) {
      setResult({ success: false, message: mutationErrorMessage(error, 'Error creating discussion') })
    }
  }

  const handleCloseSubmit = async () => {
    if (!closeSelectedPositionId) {
      setResult({ success: false, message: 'Please select a discussion' })
      return
    }
    if (!closeReason.trim()) {
      setResult({ success: false, message: 'Close note is required' })
      return
    }
    if (!closeResult) {
      setResult({ success: false, message: 'Please select profit or loss' })
      return
    }

    try {
      const response = await closePositionMutation.mutateAsync({
        positionId: closeSelectedPositionId,
        closedReason: closeReason.trim(),
        closedResult: closeResult as 'profit' | 'loss',
      })

      if (response.ok) {
        setResult({ success: true, message: 'Discussion closed successfully!' })
        setTimeout(closeModal, 1500)
      } else {
        setResult({ success: false, message: response.error || 'Failed to close discussion' })
      }
    } catch (error) {
      setResult({ success: false, message: mutationErrorMessage(error, 'Error closing discussion') })
    }
  }

  const handleUpdateSubmit = async () => {
    if (!updateSelectedPositionId) {
      setResult({ success: false, message: 'Please select a discussion' })
      return
    }

    const position = allPositions.find(p => p.id === updateSelectedPositionId)
    if (!position) {
      setResult({ success: false, message: 'Discussion not found' })
      return
    }

    // Calculate users to add/remove
    const originalUsernames = new Set(position.users_in_position.map(u => u.toLowerCase()))
    const usersToAdd: string[] = []
    const usersToRemove: string[] = []

    // Find users to add
    updateSelectedUsers.forEach(id => {
      const user = availableUsers.find(u => u.id === id)
      if (user && !originalUsernames.has(user.username.toLowerCase())) {
        usersToAdd.push(id)
      }
    })

    // Find users to remove
    position.users_in_position.forEach(username => {
      const user = availableUsers.find(u => u.username.toLowerCase() === username.toLowerCase())
      if (user && !updateSelectedUsers.has(user.id)) {
        usersToRemove.push(user.id)
      }
    })

    if (usersToAdd.length === 0 && usersToRemove.length === 0 && !updateNote.trim()) {
      setResult({ success: false, message: 'No changes to submit' })
      return
    }

    try {
      const response = await updatePositionMutation.mutateAsync({
        positionId: updateSelectedPositionId,
        payload: {
          users_to_add: usersToAdd.length > 0 ? usersToAdd : undefined,
          users_to_remove: usersToRemove.length > 0 ? usersToRemove : undefined,
          update_message: updateNote.trim() || undefined,
        },
      })

      if (response.ok) {
        const added = response.users_added?.length || 0
        const removed = response.users_removed?.length || 0
        let msg = 'Discussion updated'
        if (added > 0 || removed > 0) {
          msg += ` (${added} added, ${removed} removed)`
        }
        setResult({ success: true, message: msg })
        setTimeout(closeModal, 1500)
      } else {
        setResult({ success: false, message: response.error || 'Failed to update discussion' })
      }
    } catch (error) {
      setResult({ success: false, message: mutationErrorMessage(error, 'Error updating discussion') })
    }
  }

  const handleReopenSubmit = async () => {
    if (!reopenSelectedPositionId) {
      setResult({ success: false, message: 'Please select a closed discussion' })
      return
    }

    try {
      const response = await reopenPositionMutation.mutateAsync({
        positionId: reopenSelectedPositionId,
        payload: { reopen_reason: reopenReason.trim() || undefined },
      })

      if (response.ok) {
        setResult({ success: true, message: 'Discussion reopened successfully!' })
        setTimeout(closeModal, 1500)
      } else {
        setResult({ success: false, message: response.error || 'Failed to reopen discussion' })
      }
    } catch (error) {
      setResult({ success: false, message: mutationErrorMessage(error, 'Error reopening discussion') })
    }
  }

  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const isSubmitting = createPosition.isPending || closePositionMutation.isPending || updatePositionMutation.isPending || reopenPositionMutation.isPending

  return (
    <>
      {!hideTrigger && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 bg-teal-600 hover:bg-teal-700 text-white border-teal-700">
              <Briefcase className="h-4 w-4" />
              <span className="hidden xl:inline">Positions</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem asChild>
              <Link to="/positions">
                <List className="mr-2 h-4 w-4" />
                View All Positions
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleOpenModal('create')}>
              <TrendingUp className="mr-2 h-4 w-4 text-green-500" />
              Create Discussion
            </DropdownMenuItem>
            {hasPositionPermission && (
              <>
                <DropdownMenuItem onClick={() => handleOpenModal('update')}>
                  <FileEdit className="mr-2 h-4 w-4 text-blue-500" />
                  Update Discussion
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenModal('close')} disabled={openPositions.length === 0}>
                  <Lock className="mr-2 h-4 w-4 text-red-500" />
                  Close Discussion
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenModal('reopen')} disabled={closedPositions.length === 0}>
                  <RotateCcw className="mr-2 h-4 w-4 text-amber-500" />
                  Reopen Discussion
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Create Discussion Dialog */}
      <Dialog open={modalType === 'create'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Create Trade Discussion
            </DialogTitle>
            <DialogDescription>
              Create a new trade discussion thread.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {result && (
              <div className={`text-sm p-3 rounded-md ${result.success
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-destructive/10 text-destructive'
                }`}>
                {result.message}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="create-symbol">Symbol</Label>
              <Input
                id="create-symbol"
                placeholder="BTC, ETH, SOL..."
                value={createSymbol}
                onChange={(e) => setCreateSymbol(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label>Direction</Label>
              <Select
                value={createDirection}
                onValueChange={(value) => setCreateDirection(value as 'na' | 'long' | 'short')}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="na">N/A</SelectItem>
                  <SelectItem value="long">Long</SelectItem>
                  <SelectItem value="short">Short</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Participants</Label>
              <UserCheckboxList
                users={availableUsers}
                selectedUserIds={createSelectedUsers}
                onToggle={toggleCreateUser}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-reason">Decision Rationale</Label>
              <Textarea
                id="create-reason"
                placeholder="Explain your trading rationale..."
                value={createDecisionReason}
                onChange={(e) => setCreateDecisionReason(e.target.value)}
                rows={4}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={isSubmitting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {isSubmitting ? 'Creating...' : 'Create Position'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Position Dialog */}
      <Dialog open={modalType === 'update'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5 text-blue-500" />
              Update Discussion
            </DialogTitle>
            <DialogDescription>
              Update participants or add a saved note to an open or closed trade discussion.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {result && (
              <div className={`text-sm p-3 rounded-md ${result.success
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-destructive/10 text-destructive'
                }`}>
                {result.message}
              </div>
            )}

            <div className="space-y-2">
              <Label>Select Discussion</Label>
              <Select value={updateSelectedPositionId} onValueChange={setUpdateSelectedPositionId}>
                <SelectTrigger>
                  <SelectValue placeholder={allPositions.length === 0 ? 'No discussions' : 'Select a discussion...'} />
                </SelectTrigger>
                <SelectContent>
                  {allPositions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.symbol} · {p.status === 'closed' ? 'Closed' : 'Open'} · by {p.created_by} ({formatDate(p.created_at)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPositionForUpdate && (
              <div className="bg-muted/50 rounded-md p-3 text-sm">
                <strong>Current Users:</strong>{' '}
                {selectedPositionForUpdate.users_in_position.length > 0
                  ? selectedPositionForUpdate.users_in_position.map(u => `@${u}`).join(', ')
                  : 'None'}
              </div>
            )}

            <div className="space-y-2">
              <Label>Participants</Label>
              <UserCheckboxList
                users={availableUsers}
                selectedUserIds={updateSelectedUsers}
                onToggle={toggleUpdateUser}
                disabled={isSubmitting || !updateSelectedPositionId}
                emptyMessage={updateSelectedPositionId ? 'No users available' : 'Select a discussion first'}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="update-note">Update Note (optional)</Label>
              <Textarea
                id="update-note"
                placeholder="Add a note about this update..."
                value={updateNote}
                onChange={(e) => setUpdateNote(e.target.value)}
                rows={2}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSubmit}
              disabled={isSubmitting || !updateSelectedPositionId}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? 'Updating...' : 'Update Discussion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Discussion Dialog */}
      <Dialog open={modalType === 'close'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-red-500" />
              Close Discussion
            </DialogTitle>
            <DialogDescription>
              Close an existing trade discussion.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {result && (
              <div className={`text-sm p-3 rounded-md ${result.success
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-destructive/10 text-destructive'
                }`}>
                {result.message}
              </div>
            )}

            <div className="space-y-2">
              <Label>Select Discussion</Label>
              <Select value={closeSelectedPositionId} onValueChange={setCloseSelectedPositionId}>
                <SelectTrigger>
                  <SelectValue placeholder={openPositions.length === 0 ? 'No open discussions' : 'Select a discussion...'} />
                </SelectTrigger>
                <SelectContent>
                  {openPositions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.symbol} - by {p.created_by} ({formatDate(p.created_at)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Discussion Outcome</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="close-result"
                    value="profit"
                    checked={closeResult === 'profit'}
                    onChange={(e) => setCloseResult(e.target.value as 'profit')}
                    disabled={isSubmitting}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-green-500">✅ Profit</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="close-result"
                    value="loss"
                    checked={closeResult === 'loss'}
                    onChange={(e) => setCloseResult(e.target.value as 'loss')}
                    disabled={isSubmitting}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-red-500">❌ Loss</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="close-reason">Close Note</Label>
              <Textarea
                id="close-reason"
                placeholder="Explain why this discussion is being closed..."
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={4}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleCloseSubmit}
              disabled={isSubmitting || !closeSelectedPositionId}
              variant="destructive"
            >
              {isSubmitting ? 'Closing...' : 'Close Discussion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Discussion Dialog */}
      <Dialog open={modalType === 'reopen'} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Reopen Discussion
            </DialogTitle>
            <DialogDescription>
              Return a closed discussion to the open positions list and reopen its Telegram topic.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {result && (
              <div className={`text-sm p-3 rounded-md ${result.success
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-destructive/10 text-destructive'
                }`}>
                {result.message}
              </div>
            )}

            <div className="space-y-2">
              <Label>Select Discussion</Label>
              <Select value={reopenSelectedPositionId} onValueChange={setReopenSelectedPositionId}>
                <SelectTrigger>
                  <SelectValue placeholder={closedPositions.length === 0 ? 'No closed discussions' : 'Select a discussion...'} />
                </SelectTrigger>
                <SelectContent>
                  {closedPositions.map((position) => (
                    <SelectItem key={position.id} value={position.id}>
                      {position.symbol} · {position.closed_result === 'profit' ? 'Profit' : 'Loss'} · {position.closed_at ? formatDate(position.closed_at) : 'Closed'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reopen-reason">Reopen Note (optional)</Label>
              <Textarea
                id="reopen-reason"
                placeholder="Why is this discussion being reopened?"
                value={reopenReason}
                onChange={(event) => setReopenReason(event.target.value)}
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleReopenSubmit}
              disabled={isSubmitting || !reopenSelectedPositionId}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {isSubmitting ? 'Reopening...' : 'Reopen Discussion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
